import { useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  Pin,
  PinOff,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/ui/card";
import { Button } from "@/ui/button";
import {
  useDeleteSessionMutation,
  usePinSessionMutation,
  useSessionDetailQuery,
  useSpokenMemorySessionsQuery,
  type SpokenMemorySessionDetail,
} from "@/data/spoken-memory";
import { copyTextToClipboard } from "@/utils";

interface SessionPageProps {
  workspaceSlug: string;
  sessionId: string;
  isPinned: boolean;
  onBack: () => void;
  onPinChanged: () => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
}

export function SessionScreen({
  workspaceSlug,
  sessionId,
  isPinned,
  onBack,
  onPinChanged,
  onNavigatePrev,
  onNavigateNext,
}: SessionPageProps) {
  return (
    <AmbientSessionPage
      workspaceSlug={workspaceSlug}
      sessionId={sessionId}
      isPinned={isPinned}
      onBack={onBack}
      onPinChanged={onPinChanged}
      onNavigatePrev={onNavigatePrev}
      onNavigateNext={onNavigateNext}
    />
  );
}

function SessionFrame({
  pillKind,
  pillLabel,
  title,
  metadataLine,
  isPinned,
  onTogglePin,
  onBack,
  onPinChanged,
  onNavigatePrev,
  onNavigateNext,
  onCopyTranscript,
  onDelete,
  children,
}: {
  pillKind: "ambient" | "online" | "processing" | "attention";
  pillLabel: string;
  title: string;
  metadataLine: React.ReactNode;
  isPinned: boolean;
  onTogglePin: () => Promise<unknown>;
  onBack: () => void;
  onPinChanged: () => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  onCopyTranscript?: () => void;
  onDelete?: () => void;
  children: React.ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const handleTogglePin = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onTogglePin();
      onPinChanged();
    } catch (e) {
      window.alert(
        `Pin failed: ${e instanceof Error ? e.message : "network error"}`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-[880px] flex-col p-9">
      <div className="mb-3.5 flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-7" onClick={onBack}>
          <ArrowLeft className="h-3 w-3" />
          Back
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={onNavigatePrev}
          disabled={!onNavigatePrev}
          title="previous (k)"
          aria-label="previous (k)"
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={onNavigateNext}
          disabled={!onNavigateNext}
          title="next (j)"
          aria-label="next (j)"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
        <span className={`proto-pill pill-${pillKind}`}>{pillLabel}</span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            onClick={onCopyTranscript}
            disabled={!onCopyTranscript}
          >
            <Copy className="h-3 w-3" />
            Copy transcript
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            onClick={handleTogglePin}
            disabled={busy}
          >
            {isPinned ? (
              <PinOff className="h-3 w-3" />
            ) : (
              <Pin className="h-3 w-3" />
            )}
            {isPinned ? "Unpin" : "Pin"}
          </Button>
          {onDelete ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      <header className="mb-7">
        <h1 className="mb-2 text-[28px] font-semibold tracking-tight">
          {title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-[13px] text-muted-foreground">
          {metadataLine}
        </div>
      </header>

      {children}
    </div>
  );
}

function AmbientSessionPage({
  workspaceSlug,
  sessionId,
  isPinned,
  onBack,
  onPinChanged,
  onNavigatePrev,
  onNavigateNext,
}: SessionPageProps) {
  const detailQuery = useSessionDetailQuery(workspaceSlug, sessionId);
  const pinMutation = usePinSessionMutation(workspaceSlug);
  // The manage flag rides the (already cached/polled) sessions list response.
  const sessionsQuery = useSpokenMemorySessionsQuery(workspaceSlug);
  const canManage = sessionsQuery.data?.can_manage_spoken_memory ?? false;
  const deleteMutation = useDeleteSessionMutation(workspaceSlug);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const detail = detailQuery.data ?? null;
  const error =
    detailQuery.error instanceof Error ? detailQuery.error.message : null;

  if (error) {
    return (
      <div className="mx-auto max-w-[880px] p-9 text-[14px] text-muted-foreground">
        Could not load capture: {error}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isProcessing = detail.session.status === "processing";
  const summaryText = generatedSummaryText(detail.blocks);
  const handleCopyTranscript = async () => {
    try {
      await copyTextToClipboard(buildTranscriptText(detail));
      toast.success("Transcript copied");
    } catch {
      toast.error("Could not copy transcript");
    }
  };
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(sessionId);
      toast.success("Capture deleted");
      setConfirmDelete(false);
      onBack();
    } catch {
      toast.error("Failed to delete capture");
    }
  };

  return (
    <>
      <SessionFrame
        pillKind={isProcessing ? "processing" : "ambient"}
        pillLabel={
          isProcessing
            ? "transcribing"
            : detail.session.start_trigger === 5
              ? "meeting"
              : detail.session.mode === 2
                ? "manual"
                : "ambient"
        }
        title={detail.session.title || "Untitled capture"}
        metadataLine={
          <>
            <span>
              {detail.speaker_clusters
                .map((c) => c.matched_member_name || c.local_label)
                .join(" · ") || "—"}
            </span>
            <span>·</span>
            <span>{new Date(detail.session.started_at).toLocaleString()}</span>
            <span>·</span>
            <span>{durationLabel(detail.session)}</span>
          </>
        }
        isPinned={isPinned}
        onTogglePin={() =>
          pinMutation.mutateAsync({
            sessionId,
            pinned: !isPinned,
          })
        }
        onBack={onBack}
        onPinChanged={onPinChanged}
        onNavigatePrev={onNavigatePrev}
        onNavigateNext={onNavigateNext}
        onCopyTranscript={
          detail.segments.length > 0 ? handleCopyTranscript : undefined
        }
        onDelete={canManage ? () => setConfirmDelete(true) : undefined}
      >
        {detail.session.calendar_event ? (
          <Card className="mb-4 border-border bg-card p-6">
            <BlockLabel>Meeting</BlockLabel>
            <div className="space-y-2 text-[13px] text-foreground">
              {detail.session.calendar_event.starts_at != null &&
              detail.session.calendar_event.ends_at != null ? (
                <div className="text-muted-foreground">
                  Scheduled{" "}
                  {new Date(
                    detail.session.calendar_event.starts_at,
                  ).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                  {" – "}
                  {new Date(
                    detail.session.calendar_event.ends_at,
                  ).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              ) : null}
              {detail.session.calendar_event.attendees.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-muted-foreground">Invited:</span>
                  {detail.session.calendar_event.attendees.map((attendee) => (
                    <span
                      key={attendee.email}
                      className="rounded-full border border-border bg-background px-2 py-0.5 text-[12px]"
                      title={attendee.response_status}
                    >
                      {attendee.email}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {summaryText ? (
          <Card className="mb-4 border-border bg-card p-6">
            <BlockLabel>Summary</BlockLabel>
            <div className="space-y-3 text-[15px] leading-relaxed whitespace-pre-wrap text-foreground">
              {summaryText}
            </div>
          </Card>
        ) : null}

        <Card className="mb-4 border-border bg-card p-6">
          <BlockLabel
            right={`${detail.segments.length} segments · ${detail.speaker_clusters.length} speakers`}
          >
            {isProcessing ? "Live transcript" : "Transcript"}
          </BlockLabel>
          <div className="flex flex-col gap-4">
            {detail.segments.map((seg) => (
              <div
                key={seg.id}
                className="grid grid-cols-[120px_1fr] gap-3 text-[13px] leading-relaxed"
              >
                <div className="font-mono text-muted-foreground">
                  <span className="proto-speaker">
                    {seg.speaker_name || seg.local_label || "Unknown"}
                  </span>
                  <br />
                  <span className="mt-1 inline-block text-[11px] text-muted-foreground/80">
                    {formatClock(detail.session.started_at + seg.start_ms)}
                  </span>
                </div>
                <div className="text-foreground/90">{seg.text}</div>
              </div>
            ))}
            {isProcessing ? (
              <div className="grid grid-cols-[120px_1fr] gap-3 text-[13px] text-muted-foreground/60 italic">
                <div>…</div>
                <div>transcribing in real time</div>
              </div>
            ) : null}
          </div>
        </Card>
      </SessionFrame>
      {confirmDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-capture-title"
            className="w-full max-w-md rounded-lg border border-border bg-card shadow-2xl"
          >
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <TriangleAlert className="h-4 w-4" />
              </span>
              <h3
                id="delete-capture-title"
                className="text-[14px] font-semibold text-foreground"
              >
                Delete this capture?
              </h3>
            </div>
            <div className="space-y-2 px-4 py-4 text-[13px] text-muted-foreground">
              <p>
                The transcript, summary, speaker data, and audio for “
                {detail.session.title || "Untitled capture"}” are permanently
                deleted for everyone in the workspace.
              </p>
              <p>This can't be undone.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={deleteMutation.isPending}
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => void handleDelete()}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                Delete capture
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function generatedSummaryText(
  blocks: SpokenMemorySessionDetail["blocks"],
): string {
  return blocks
    .map((block) => block.summary.trim())
    .filter((summary, index) => {
      if (!summary) return false;
      return (
        normalizeSummaryComparable(summary) !==
        normalizeSummaryComparable(blocks[index]?.text ?? "")
      );
    })
    .join("\n\n");
}

function normalizeSummaryComparable(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function BlockLabel({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
      <span>{children}</span>
      {right ? (
        <span className="text-[11px] font-medium tracking-normal normal-case">
          {right}
        </span>
      ) : null}
    </div>
  );
}

function buildTranscriptText(detail: SpokenMemorySessionDetail): string {
  return detail.segments
    .map((seg) => {
      const speaker = seg.speaker_name || seg.local_label || "Unknown";
      const time = formatClock(detail.session.started_at + seg.start_ms);
      return `[${time}] ${speaker}: ${seg.text}`;
    })
    .join("\n");
}

function durationLabel(session: {
  started_at: number;
  ended_at: number | null;
}): string {
  if (session.ended_at == null) return "in progress";
  const ms = session.ended_at - session.started_at;
  if (ms <= 0) return "in progress";
  const minutes = Math.round(ms / 60_000);
  return `${minutes} min`;
}

function formatClock(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}
